import React, { useState, useEffect, useMemo } from 'react';
import { Download, Users, Package, Ship, Calendar, TrendingUp, IndianRupee, Loader2 } from 'lucide-react';
import { useRole } from '../../components/MangeContextProvide';
import { axiosInstance } from '../../utility/apiInstance';
import './usage-metrics.css';
import toast from 'react-hot-toast';


interface SummaryData {
  air: {
    count: number;
    cost: number;
    activeUsers: number;
  };
  ocean: {
    count: number;
    cost: number;
    activeUsers: number;
  };
  totalTracking: number;
  totalCost: number;
  activeUsers: number;
}

interface UserData {
  id: string;
  name: string;
  email: string;
  awbCount: number;
  oceanCount: number;
  totalCount: number;
  cost: number;
  lastActivity: string;
  contribution: number;
}

interface TrackingItem {
  number: string;
  date: string;
  cost: number;
}

interface UserDetailsResponse {
  user: {
    id: string;
    name: string;
    email: string;
  };
  awb: TrackingItem[];
  ocean: TrackingItem[];
}

interface ReportResponse {
  reportType: string;
  dateRange: string;
  summary: {
    air: {
      count: number;
      cost: number;
      activeUsers: number;
    };
    ocean: {
      count: number;
      cost: number;
      activeUsers: number;
    };
    total: {
      count: number;
      cost: number;
      activeUsers: number;
    };
  };
}



const UsageMetricsDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<string>('this_month');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { organizationId } = useRole() as any;


  const fetchSummary = async (): Promise<void> => {
    try {
      const res = await axiosInstance.get(`api/v1/report/summary?organizationId=${organizationId}&dateRange=${dateRange}`);
      const data: SummaryData = res.data.data;
      setSummaryData(data);
    } catch (err) {
      throw new Error('Summary fetch failed: ' + (err as Error).message);
    }
  };

  const fetchUsers = async (): Promise<void> => {
    try {
      const res = await axiosInstance.get(`api/v1/report/users?organizationId=${organizationId}&dateRange=${dateRange}&search=${searchTerm}`);
      const data: UserData[] = res.data.data;
      setUserData(data);
    } catch (err) {
      throw new Error('User fetch failed: ' + (err as Error).message);
    }
  };

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchSummary(), fetchUsers()]);
      } catch (err) {
        setError((err as Error).message);
      }
      setLoading(false);
    };
    loadData();
  }, [dateRange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return userData;
    const search = searchTerm.toLowerCase();
    return userData.filter(user =>
      user.name.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search)
    );
  }, [userData, searchTerm]);

  const handleDownloadUserReport = async (user: UserData): Promise<void> => {
    try {
      const res = await axiosInstance.get(
        `api/v1/report/user-details/${user.id}?organizationId=${organizationId}&dateRange=${dateRange}`
      );

      const data: UserDetailsResponse = res.data?.data;

      if (!data?.user) {
        toast.error("No user data available");
        return;
      }

      const awb = Array.isArray(data.awb) ? data.awb : [];
      const ocean = Array.isArray(data.ocean) ? data.ocean : [];

      // CSV-safe value (handles commas, quotes, new lines)
      const csvSafe = (value: any) =>
        `"${String(value ?? "").replace(/"/g, '""')}"`;

      const rows: string[][] = [
        ['User Name', data.user.name ?? ''],
        ['Email', data.user.email ?? ''],
        [''],
        ['Date Range', dateRange.replace('_', ' ').toUpperCase()],
        ['Total AWB', String(awb.length)],
        ['Total Ocean', String(ocean.length)],
        ['Total Cost (INR)', Number(user.cost ?? 0).toLocaleString('en-IN')],
        [''],
        ['Type', 'Tracking Number', 'Added Date', 'Cost (INR)']
      ];

      if (awb.length === 0 && ocean.length === 0) {
        rows.push(['-', 'No tracking data available', '-', '-']);
      } else {
        awb.forEach(item =>
          rows.push([
            'AWB',
            item.number ?? '',
            item.date ?? '',
            String(item.cost ?? 0)
          ])
        );

        ocean.forEach(item =>
          rows.push([
            'Ocean',
            item.number ?? '',
            item.date ?? '',
            String(item.cost ?? 0)
          ])
        );
      }

      const csvContent = rows
        .map(row => row.map(csvSafe).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const fileName =
        (data.user.name || 'user').replace(/\s+/g, '_') +
        `_tracking_report_${dateRange}.csv`;

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      URL.revokeObjectURL(url);
      toast.success("Report downloaded successfully");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download report");
    }
  };

  const handleDownloadAllReport = async (): Promise<void> => {
    try {
      const res = await axiosInstance.post(`api/v1/report/download-report`, {
        organizationId,
        dateRange
      });

      const data: ReportResponse = res.data.data;

      const air = data.summary?.air;
      const ocean = data.summary?.ocean;
      const total = data.summary?.total;

      const csvRows: string[][] = [
        ['Usage Metrics Report'],
        ['Date Range', dateRange.replace('_', ' ').toUpperCase()],
        [''],

        ['Air Tracking Summary'],
        ['Total AWB Count', String(air?.count ?? 0)],
        ['Total Cost (INR)', Number(air?.cost ?? 0).toLocaleString('en-IN')],
        ['Active Users', String(air?.activeUsers ?? 0)],
        [''],

        ['Ocean Tracking Summary'],
        ['Total Ocean Count', String(ocean?.count ?? 0)],
        ['Total Cost (INR)', Number(ocean?.cost ?? 0).toLocaleString('en-IN')],
        ['Active Users', String(ocean?.activeUsers ?? 0)],
        [''],

        ['Overall Summary'],
        ['Total Tracking Numbers', String((air?.count ?? 0) + (ocean?.count ?? 0))],
        ['Total Cost (INR)', Number(total?.cost ?? 0).toLocaleString('en-IN')],
        ['Total Active Users', String(total?.activeUsers ?? 0)],
        ['']
      ];

      // ✅ Add user-wise data ONLY if available
      if (Array.isArray(userData) && userData.length > 0) {
        csvRows.push(
          ['User Name', 'Email', 'AWB Count', 'Ocean Count', 'Total', 'Cost (INR)', 'Contribution %', 'Last Activity'],
          ...userData.map(user => [
            user.name ?? '',
            user.email ?? '',
            String(user.awbCount ?? 0),
            String(user.oceanCount ?? 0),
            String(user.totalCount ?? 0),
            Number(user.cost ?? 0).toLocaleString('en-IN'),
            `${user.contribution ?? 0}%`,
            user.lastActivity ?? ''
          ])
        );
      }

      const csvContent = csvRows.map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `usage_metrics_report_${dateRange}.csv`;
      a.click();

      URL.revokeObjectURL(url);
      toast.success("Report downloaded successfully");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Failed to download");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-6 rounded-lg shadow-sm max-w-md">
          <div className="text-red-500 text-4xl mb-3">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Connection Error</h3>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <div className="space-y-2">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium"
            >
              Retry
            </button>
            <p className="text-xs text-gray-500">
              Make sure backend is running on port 3001
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="usageMetricsMain">
        <div className="headerUsageMetrics flex items-center justify-between">
          <div className="headerUsageMetricsText">
            <h1>Usage Metrics</h1>
            <p>Monitor tracking number additions and user activity</p>
          </div>
          <button
            onClick={handleDownloadAllReport}
            className="btn btnYellow"
          >
            <Download className="w-3.5 h-3.5" />
            Download Report
          </button>
        </div>

        <div className="usageMatricsFirst">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <div className="usageMatricsCalendar">
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>

              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
              </select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {summaryData && (
          <div className="usageMatricsCardOuter">
            <div className="usageMatricsCard usageMatricsCard_1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700 text-sm font-semibold">Air Tracking</span>
                <figure className="light-orange"><Package className="text-orange-500" /></figure>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">{summaryData.air.count}</span>
                  <span className="text-xs text-gray-500">AWB numbers</span>
                </div>
                <div className="flex items-center gap-1 text-red-600">
                  <IndianRupee className="w-3.5 h-3.5" />
                  <span className="text-lg font-semibold">{summaryData.air.cost.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600 text-xs">
                  <Users className="w-3 h-3" />
                  <span>{summaryData.air.activeUsers} active users</span>
                </div>
              </div>
            </div>

            <div className="usageMatricsCard usageMatricsCard_2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700 text-sm font-semibold">Ocean Tracking</span>
                <figure className="light-green"><Ship className="text-green-500" /></figure>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">{summaryData.ocean.count}</span>
                  <span className="text-xs text-gray-500">Ocean numbers</span>
                </div>
                <div className="flex items-center gap-1 text-red-600">
                  <IndianRupee className="w-3.5 h-3.5" />
                  <span className="text-lg font-semibold">{summaryData.ocean.cost.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600 text-xs">
                  <Users className="w-3 h-3" />
                  <span>{summaryData.ocean.activeUsers} active users</span>
                </div>
              </div>
            </div>

            <div className="usageMatricsCard usageMatricsCard_3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700 text-sm font-semibold">Total Summary</span>
                <figure className="light-blue"><TrendingUp className="w-5 h-5 text-blue-500" /></figure>
              </div>
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">{summaryData.air.count + summaryData.ocean.count}</span>
                  <span className="text-xs text-gray-500">All tracking</span>
                </div>
                <div className="flex items-center gap-1 text-red-600">
                  <IndianRupee className="w-3.5 h-3.5" />
                  <span className="text-lg font-semibold">{summaryData.totalCost.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-600 text-xs">
                  <Users className="w-3 h-3" />
                  <span>{summaryData.activeUsers} active users</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="whiteBox">
          <div className="usageTableHeading">
            <h2>User-Wise Tracking Activity</h2>
          </div>

          <div className="usageTableResponsive">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>AWB</th>
                  <th>Ocean</th>
                  <th>Total</th>
                  <th>Cost (INR)</th>
                  <th>Contribution</th>
                  <th>Last Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userData.length > 0 && userData.map((user) => {
                  const cellBorder = { borderBottom: "1px solid rgba(224, 224, 224, 1)" };
                  return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td style={cellBorder}>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td style={cellBorder}>
                      <div className="flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 text-orange-500" />
                        <span className="text-sm font-medium text-gray-900">{user.awbCount}</span>
                      </div>
                    </td>
                    <td style={cellBorder}>
                      <div className="flex items-center gap-1.5">
                        <Ship className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-sm font-medium text-gray-900">{user.oceanCount}</span>
                      </div>
                    </td>
                    <td style={cellBorder}>
                      <span className="text-sm font-bold text-gray-900">{user.totalCount}</span>
                    </td>
                    <td style={cellBorder}>
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-3 h-3 text-red-500" />
                        <span className="text-sm font-medium text-gray-900">{user.cost.toLocaleString('en-IN')}</span>
                      </div>
                    </td>
                    <td style={cellBorder}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[80px]">
                          <div
                            className="bg-yellow-500 h-1.5 rounded-full"
                            style={{ width: `${user.contribution}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{user.contribution}%</span>
                      </div>
                    </td>
                    <td style={cellBorder}>
                      <span className="text-xs text-gray-600">{user.lastActivity}</span>
                    </td>
                    <td style={cellBorder}>
                      <button
                        onClick={() => handleDownloadUserReport(user)}
                        className="flex items-center gap-1 text-yellow-500 hover:text-yellow-600 text-xs font-medium transition-colors"
                      >
                        <Download/>
                        Download
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {userData.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No users found matching your search</p>
            </div>
          )}


          <div className="mt-3 bg-white rounded shadow-sm p-2">
            <div className="flex items-center justify-between text-xs text-gray-600 usageTableFooter">
              <span>Showing {userData.length} of {userData.length} users</span>
              <span>Period: {dateRange.replace('_', ' ').toUpperCase()}</span>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default UsageMetricsDashboard;